import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from "@angular/core";
import { JsonPipe, NgTemplateOutlet } from "@angular/common";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from "@angular/material/dialog";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatCard, MatCardActions, MatCardContent } from "@angular/material/card";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatOption } from "@angular/material/core";
import { MatSelect } from "@angular/material/select";
import { MatTooltip } from "@angular/material/tooltip";
import { ColorSwatchPickerComponent } from "../../shared-components/color-swatch-picker/color-swatch-picker.component";
import { PropertyTreePickerComponent } from "../../shared-components/property-tree-picker/property-tree-picker.component";
import { ValueInputWidgetComponent, ValueKind } from "../../shared-components/value-input-widget/value-input-widget.component";
import { IconStylePickerComponent } from "../../shared-components/icon-style-picker/icon-style-picker.component";
import { Viewerlayer } from "../../viewer-components/model/viewerlayer";
import { ViewerConstants } from "../../shared/model/viewer-constants";
import { IFlatStyle, IRulesStyle, IStylePropertyValue, IStyleRule, IVectorStyle } from "../../shared/model/vector-style-definition.interface";
import {
  DASH_PRESET_OPTIONS,
  DASH_PRESET_PATTERNS,
  DashPreset,
  FilterOperator,
  IConditionLeaf,
  IRuleCondition,
  OPERATORS,
  RuleCombinator,
} from "../../shared/model/vector-style-condition.model";
import { conditionToFilter, filterToCondition, unwrapGetExpression } from "../../shared/utils/vector-style-condition.util";
import {
  buildPropertyTree,
  detectValueKind,
  distinctStringOptions,
  IPropertyTreeNode,
  sampleValuesForPath,
  SampleFeature,
} from "../../shared/utils/property-inspector.util";
import { resolveIconKeyFromDataUri, svgToDataUri } from "../../shared/utils/svg-icon-data-uri.util";
import { SVG_ICONS_DICTIONARY } from "../../shared/icons/svg-icons-dictionary";

/** Caps how many of a layer's features are scanned for property-type/enum sampling. */
const MAX_SAMPLE_FEATURES = 200;

const DEFAULT_FILL_COLOR = "rgba(49,159,211,0.4)";
const DEFAULT_STROKE_COLOR = "rgba(49,159,211,1)";
const DEFAULT_CIRCLE_FILL_COLOR = "rgba(49,159,211,0.8)";
const DEFAULT_CIRCLE_STROKE_COLOR = "rgba(255,255,255,1)";

/** Closed set of text styles — always rendered in Roboto, never a user-editable free-text font. */
export type TextFontStyle = "normal" | "bold" | "light" | "italic";

const FONT_STYLE_OPTIONS: { value: TextFontStyle; label: string }[] = [
  { value: "normal", label: "Normal" },
  { value: "bold", label: "Bold" },
  { value: "light", label: "Light" },
  { value: "italic", label: "Italic" },
];

/** Canvas font-shorthand prefix per style. Order matters for reverse-matching: check longer/more specific
 *  prefixes before "normal" (empty string), which would otherwise match every font string. */
const FONT_STYLE_CSS_PREFIX: Record<TextFontStyle, string> = {
  bold: "bold ",
  light: "300 ",
  italic: "italic ",
  normal: "",
};

const FONT_FAMILY = "Roboto, sans-serif";

/** Finds the dash preset whose pattern exactly matches `dash`, or "custom" when it doesn't match any. */
function matchDashPreset(dash: number[] | undefined): DashPreset {
  if (!dash) return "solid";
  const match = (Object.entries(DASH_PRESET_PATTERNS) as [Exclude<DashPreset, "custom">, number[] | undefined][]).find(
    ([, pattern]) => !!pattern && pattern.length === dash.length && pattern.every((v, i) => v === dash[i]),
  );
  return match ? match[0] : "custom";
}

/**
 * OL's `Icon`/`Circle` image styles only render on Point/MultiPoint geometries — showing the Icon
 * and Point marker sections for a layer with no point features would just be dead controls that
 * silently do nothing when saved. `features` is the same raw GeoJSON-shaped array used elsewhere
 * in this component (has `.geometry`, unlike the narrower `SampleFeature` type).
 */
function hasPointGeometry(features: { geometry?: { type?: string } }[]): boolean {
  return features.some(
    (f) => f?.geometry?.type === ViewerConstants.GEOMETRY_TYPE.Point || f?.geometry?.type === ViewerConstants.GEOMETRY_TYPE.MultiPoint,
  );
}

/**
 * Parses OL's `text-font` CSS shorthand this dialog itself writes (`"{prefix}{size}px Roboto, sans-serif"`).
 * A stored value that doesn't match any known style prefix (legacy free-text font, AI-generated style)
 * falls back to "normal" — a cosmetic reset only; property path/colors/halo are unaffected.
 */
function parseFont(font: unknown): [number | undefined, TextFontStyle] {
  if (typeof font !== "string") return [undefined, "normal"];
  const sizeMatch = /(\d+(?:\.\d+)?)px/.exec(font);
  const size = sizeMatch ? Number(sizeMatch[1]) : undefined;
  const style = (Object.keys(FONT_STYLE_CSS_PREFIX) as TextFontStyle[]).find(
    (key) => key !== "normal" && font.startsWith(FONT_STYLE_CSS_PREFIX[key]),
  );
  return [size, style ?? "normal"];
}

/** Sub-form shared by the default style and every rule card — same set of visual properties either way. */
function createStylePropsGroup(): FormGroup {
  return new FormGroup({
    fillColor: new FormControl(DEFAULT_FILL_COLOR, { nonNullable: true }),
    strokeColor: new FormControl(DEFAULT_STROKE_COLOR, { nonNullable: true }),
    strokeWidth: new FormControl(1, { nonNullable: true, validators: [Validators.min(0)] }),
    strokeDashPreset: new FormControl<DashPreset>("solid", { nonNullable: true }),
    strokeDashCustomValue: new FormControl<number[] | undefined>(undefined),
    includeCircle: new FormControl(false, { nonNullable: true }),
    circleRadius: new FormControl(6, { nonNullable: true, validators: [Validators.min(1)] }),
    circleFillColor: new FormControl(DEFAULT_CIRCLE_FILL_COLOR, { nonNullable: true }),
    circleStrokeColor: new FormControl(DEFAULT_CIRCLE_STROKE_COLOR, { nonNullable: true }),
    text: new FormGroup({
      enabled: new FormControl(false, { nonNullable: true }),
      propertyPath: new FormControl<string[]>([], { nonNullable: true }),
      fontStyle: new FormControl<TextFontStyle>("normal", { nonNullable: true }),
      fontSize: new FormControl(12, { nonNullable: true, validators: [Validators.min(1)] }),
      fillColor: new FormControl("#000000", { nonNullable: true }),
      haloColor: new FormControl("#ffffff", { nonNullable: true }),
      haloWidth: new FormControl(2, { nonNullable: true, validators: [Validators.min(0)] }),
      /** Default-style-only: when true, this label is forced onto every rule, ignoring each rule's own text config. */
      applyToAll: new FormControl(false, { nonNullable: true }),
    }),
    icon: new FormGroup({
      enabled: new FormControl(false, { nonNullable: true }),
      key: new FormControl<string | undefined>(undefined),
      iconSrcRaw: new FormControl<string | undefined>(undefined),
      scale: new FormControl(1, { nonNullable: true, validators: [Validators.min(0.1)] }),
      color: new FormControl<string | undefined>(undefined),
      rotation: new FormControl(0, { nonNullable: true }),
    }),
  });
}

function buildFlatProperties(group: FormGroup): Record<string, IStylePropertyValue> {
  const v = group.value;
  const props: Record<string, IStylePropertyValue> = {
    "fill-color": v.fillColor,
    "stroke-color": v.strokeColor,
    "stroke-width": v.strokeWidth,
  };

  if (v.strokeDashPreset === "custom") {
    if (v.strokeDashCustomValue) props["stroke-line-dash"] = v.strokeDashCustomValue;
  } else {
    const pattern = DASH_PRESET_PATTERNS[v.strokeDashPreset as Exclude<DashPreset, "custom">];
    if (pattern) props["stroke-line-dash"] = pattern;
  }

  if (v.includeCircle) {
    props["circle-radius"] = v.circleRadius;
    props["circle-fill-color"] = v.circleFillColor;
    props["circle-stroke-color"] = v.circleStrokeColor;
  }

  if (v.text?.enabled) {
    // to-string coerces non-string property values (numbers, booleans) — OL's text-value throws
    // "Expected a string for text-value" at render time otherwise.
    props["text-value"] = ["to-string", ["get", ...v.text.propertyPath]];
    props["text-font"] = `${FONT_STYLE_CSS_PREFIX[v.text.fontStyle as TextFontStyle]}${v.text.fontSize}px ${FONT_FAMILY}`;
    props["text-fill-color"] = v.text.fillColor;
    props["text-stroke-color"] = v.text.haloColor;
    props["text-stroke-width"] = v.text.haloWidth;
  }

  if (v.icon?.enabled) {
    const iconSrc = v.icon.key ? svgToDataUri(SVG_ICONS_DICTIONARY[v.icon.key]) : v.icon.iconSrcRaw;
    if (iconSrc) {
      props["icon-src"] = iconSrc;
      props["icon-scale"] = v.icon.scale;
      props["icon-rotation"] = v.icon.rotation;
      if (v.icon.color) props["icon-color"] = v.icon.color;
    }
  }

  return props;
}

function patchStylePropsGroup(group: FormGroup, properties: Record<string, any> | undefined): void {
  if (!properties) return;

  const dash = properties["stroke-line-dash"] as number[] | undefined;
  const dashPreset = matchDashPreset(dash);

  group.patchValue({
    fillColor: properties["fill-color"] ?? DEFAULT_FILL_COLOR,
    strokeColor: properties["stroke-color"] ?? DEFAULT_STROKE_COLOR,
    strokeWidth: properties["stroke-width"] ?? 1,
    strokeDashPreset: dashPreset,
    strokeDashCustomValue: dashPreset === "custom" ? dash : undefined,
    includeCircle: properties["circle-radius"] !== undefined,
    circleRadius: properties["circle-radius"] ?? 6,
    circleFillColor: properties["circle-fill-color"] ?? DEFAULT_CIRCLE_FILL_COLOR,
    circleStrokeColor: properties["circle-stroke-color"] ?? DEFAULT_CIRCLE_STROKE_COLOR,
  });

  const textPropertyPath = unwrapGetExpression(properties["text-value"]) ?? [];
  const [fontSize, fontStyle] = parseFont(properties["text-font"]);
  (group.get("text") as FormGroup).patchValue({
    enabled: properties["text-value"] !== undefined,
    propertyPath: textPropertyPath,
    fontStyle,
    fontSize: fontSize ?? 12,
    fillColor: properties["text-fill-color"] ?? "#000000",
    haloColor: properties["text-stroke-color"] ?? "#ffffff",
    haloWidth: properties["text-stroke-width"] ?? 2,
  });

  (group.get("icon") as FormGroup).patchValue({
    enabled: properties["icon-src"] !== undefined,
    key: resolveIconKeyFromDataUri(properties["icon-src"], SVG_ICONS_DICTIONARY),
    iconSrcRaw: properties["icon-src"],
    scale: properties["icon-scale"] ?? 1,
    color: properties["icon-color"],
    rotation: properties["icon-rotation"] ?? 0,
  });
}

function createLeafGroup(leaf?: IConditionLeaf): FormGroup {
  return new FormGroup({
    propertyPath: new FormControl<string[]>(leaf?.propertyPath ?? [], { nonNullable: true }),
    operator: new FormControl<FilterOperator>(leaf?.operator ?? ">", { nonNullable: true }),
    value: new FormControl<string | number | boolean | (string | number)[]>(leaf?.value ?? "", { nonNullable: true }),
  });
}

function createRuleGroup(condition?: IRuleCondition): FormGroup {
  return new FormGroup({
    combinator: new FormControl<RuleCombinator>(condition?.combinator ?? "all", { nonNullable: true }),
    conditions: new FormArray<FormGroup>((condition?.conditions ?? []).map((leaf) => createLeafGroup(leaf))),
    unsupported: new FormControl<boolean>(condition?.unsupported ?? false, { nonNullable: true }),
    rawFilter: new FormControl<unknown[] | undefined>(condition?.raw),
    styleProps: createStylePropsGroup(),
  });
}

function readRuleCondition(group: FormGroup): IRuleCondition {
  if (group.get("unsupported")!.value) {
    return { combinator: "all", conditions: [], unsupported: true, raw: group.get("rawFilter")!.value as unknown[] };
  }
  const conditions = (group.get("conditions") as FormArray<FormGroup>).controls.map(
    (leafGroup): IConditionLeaf => ({
      propertyPath: leafGroup.get("propertyPath")!.value,
      operator: leafGroup.get("operator")!.value,
      value: leafGroup.get("value")!.value,
    }),
  );
  return { combinator: group.get("combinator")!.value, conditions };
}

const TEXT_PROPERTY_KEYS = ["text-value", "text-font", "text-fill-color", "text-stroke-color", "text-stroke-width"] as const;

/** Picks just the text-* keys out of an already-built properties object (used to force the Default label onto every rule). */
function extractTextProperties(properties: Record<string, IStylePropertyValue>): Record<string, IStylePropertyValue> {
  const result: Record<string, IStylePropertyValue> = {};
  for (const key of TEXT_PROPERTY_KEYS) {
    if (properties[key] !== undefined) result[key] = properties[key];
  }
  return result;
}

/** `textOverride`, when given, replaces this rule's own text-* properties with the Default style's — see "Apply to all features". */
function buildConditionRule(group: FormGroup, textOverride: Record<string, IStylePropertyValue> | null): IStyleRule {
  const filter = conditionToFilter(readRuleCondition(group));
  const style = buildFlatProperties(group.get("styleProps") as FormGroup);
  if (textOverride) Object.assign(style, textOverride);
  return filter !== undefined ? { filter, style } : { style };
}

/**
 * Whether a rule/default styleProps group's text and icon sub-sections are internally consistent
 * (enabled implies a selection). `skipTextValidation` is used for a rule whose label is being
 * forced from the Default style ("Apply to all features") — its own text config is irrelevant then.
 */
function isStylePropsValid(group: FormGroup, skipTextValidation = false): boolean {
  const text = group.get("text") as FormGroup;
  const icon = group.get("icon") as FormGroup;
  const textValid = skipTextValidation || !text.get("enabled")!.value || (text.get("propertyPath")!.value as string[]).length > 0;
  const iconValid = !icon.get("enabled")!.value || !!icon.get("key")!.value || !!icon.get("iconSrcRaw")!.value;
  return textValid && iconValid;
}

/**
 * Advanced editor for a layer's `styleDefinition` (flat/rules vector style, see
 * shared/model/vector-style-definition.interface.ts). Separate from EditLayerDialogComponent,
 * which only edits the legacy fill/stroke/opacity ILayerStyleVector.
 *
 * UI model: one always-present "default style" card plus zero or more conditional "rule" cards.
 * The default card is what gets emitted as the trailing `else` rule (or as the whole `flat` style
 * when there are no rule cards) — the wire-format concept of an "else" rule is never exposed to the user.
 * Each rule's condition is one combinator (ALL/ANY) applied to a flat list of leaf comparisons — a
 * filter that doesn't fit this shape (nested compounds, match/case/between, ...) is preserved verbatim
 * as a read-only "unsupported" condition rather than corrupted or dropped.
 */
@Component({
  selector: "lib-edit-vector-style-dialog",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    NgTemplateOutlet,
    JsonPipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIconButton,
    MatCard,
    MatCardActions,
    MatCardContent,
    MatCheckbox,
    MatFormField,
    MatLabel,
    MatIcon,
    MatInput,
    MatOption,
    MatSelect,
    MatTooltip,
    ColorSwatchPickerComponent,
    PropertyTreePickerComponent,
    ValueInputWidgetComponent,
    IconStylePickerComponent,
  ],
  templateUrl: "./edit-vector-style-dialog.component.html",
  styleUrl: "./edit-vector-style-dialog.component.scss",
})
export class EditVectorStyleDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<EditVectorStyleDialogComponent>);

  private readonly cdr = inject(ChangeDetectorRef);

  readonly data = inject<{ layer: Viewerlayer }>(MAT_DIALOG_DATA);

  readonly operators = OPERATORS;

  readonly dashPresetOptions = DASH_PRESET_OPTIONS;

  readonly fontStyleOptions = FONT_STYLE_OPTIONS;

  readonly propertyTree: IPropertyTreeNode[];

  /** Whether the layer has any Point/MultiPoint features — gates the Icon and Point marker sections. */
  readonly hasPointFeatures: boolean;

  usedColors: string[] = [];

  private readonly features: SampleFeature[];

  readonly form = new FormGroup({
    default: createStylePropsGroup(),
    rules: new FormArray<FormGroup>([]),
  });

  get rules(): FormArray<FormGroup> {
    return this.form.get("rules") as FormArray<FormGroup>;
  }

  /** True when the Default style's label is forced onto every rule — locks each rule's own text section. */
  get labelAppliesToAll(): boolean {
    return !!(this.form.get("default") as FormGroup).get("text")!.get("applyToAll")!.value;
  }

  get isFormValid(): boolean {
    const skipRuleText = this.labelAppliesToAll;
    const defaultValid = isStylePropsValid(this.form.get("default") as FormGroup);
    const rulesValid = this.rules.controls.every((rule) => {
      const styleProps = rule.get("styleProps") as FormGroup;
      if (rule.get("unsupported")!.value) return isStylePropsValid(styleProps, skipRuleText);
      return this.conditionsOf(rule).length > 0 && isStylePropsValid(styleProps, skipRuleText);
    });
    return defaultValid && rulesValid;
  }

  /** Human-readable reason `isFormValid` is false, shown as a tooltip on the disabled Save button. */
  get saveDisabledReason(): string {
    const skipRuleText = this.labelAppliesToAll;
    const defaultProps = this.form.get("default") as FormGroup;
    if (!isStylePropsValid(defaultProps)) return this.missingReason(defaultProps, "Default style");

    const invalidRuleIndex = this.rules.controls.findIndex((rule) => {
      const styleProps = rule.get("styleProps") as FormGroup;
      if (rule.get("unsupported")!.value) return !isStylePropsValid(styleProps, skipRuleText);
      return this.conditionsOf(rule).length === 0 || !isStylePropsValid(styleProps, skipRuleText);
    });
    if (invalidRuleIndex === -1) return "";

    const rule = this.rules.at(invalidRuleIndex);
    if (this.conditionsOf(rule).length === 0) return `Rule ${invalidRuleIndex + 1}: add at least one condition`;
    return this.missingReason(rule.get("styleProps") as FormGroup, `Rule ${invalidRuleIndex + 1}`);
  }

  private missingReason(styleProps: FormGroup, label: string): string {
    const text = styleProps.get("text") as FormGroup;
    const icon = styleProps.get("icon") as FormGroup;
    if (text.get("enabled")!.value && !(text.get("propertyPath")!.value as string[]).length) {
      return `${label}: select a text property`;
    }
    if (icon.get("enabled")!.value && !icon.get("key")!.value && !icon.get("iconSrcRaw")!.value) {
      return `${label}: select an icon`;
    }
    return `${label}: incomplete`;
  }

  constructor() {
    this.features = (this.data.layer?.source?.features?.features ?? []).slice(0, MAX_SAMPLE_FEATURES);
    this.propertyTree = buildPropertyTree(this.features);
    this.hasPointFeatures = hasPointGeometry(this.features as { geometry?: { type?: string } }[]);

    const styleDefinition = this.data.layer?.styleDefinition;
    if (!styleDefinition) return;

    if (styleDefinition.type === "flat") {
      patchStylePropsGroup(this.form.get("default") as FormGroup, styleDefinition.properties);
      return;
    }

    const rules = [...styleDefinition.rules];
    const last = rules[rules.length - 1];
    if (last?.else) {
      patchStylePropsGroup(this.form.get("default") as FormGroup, last.style);
      rules.pop();
    }

    rules.forEach((rule) => {
      const condition = filterToCondition(rule.filter as unknown[] | undefined | null);
      const group = createRuleGroup(condition);
      patchStylePropsGroup(group.get("styleProps") as FormGroup, rule.style);
      this.rules.push(group);
    });
  }

  /** Compact read-only summary of the Default label, shown on a rule card when its own text section is locked. */
  defaultTextSummary(): string {
    const text = (this.form.get("default") as FormGroup).get("text") as FormGroup;
    const styleLabel = this.fontStyleOptions.find((o) => o.value === text.get("fontStyle")!.value)?.label ?? "Normal";
    const path = (text.get("propertyPath")!.value as string[]).join(".") || "—";
    return `${path} · ${styleLabel} ${text.get("fontSize")!.value}px · ${text.get("fillColor")!.value}`;
  }

  conditionsOf(rule: FormGroup): FormArray<FormGroup> {
    return rule.get("conditions") as FormArray<FormGroup>;
  }

  addRule(): void {
    const group = createRuleGroup();
    this.conditionsOf(group).push(createLeafGroup());
    this.rules.push(group);
    this.cdr.markForCheck();
  }

  removeRule(index: number): void {
    this.rules.removeAt(index);
    this.cdr.markForCheck();
  }

  addCondition(rule: FormGroup): void {
    this.conditionsOf(rule).push(createLeafGroup());
    this.cdr.markForCheck();
  }

  removeCondition(rule: FormGroup, index: number): void {
    this.conditionsOf(rule).removeAt(index);
    this.cdr.markForCheck();
  }

  onLeafPropertySelected(leafGroup: FormGroup, path: string[]): void {
    leafGroup.get("propertyPath")!.setValue(path);
    this.cdr.markForCheck();
  }

  onTextPropertySelected(styleProps: FormGroup, path: string[]): void {
    (styleProps.get("text") as FormGroup).get("propertyPath")!.setValue(path);
    this.cdr.markForCheck();
  }

  onIconKeySelected(styleProps: FormGroup, key: string): void {
    (styleProps.get("icon") as FormGroup).get("key")!.setValue(key);
    this.cdr.markForCheck();
  }

  /** Single sample scan per leaf per render — avoids scanning `this.features` twice (once for kind, once for options). */
  getLeafValueInfo(leafGroup: FormGroup): { kind: ValueKind; options: string[] | null } {
    const path = leafGroup.get("propertyPath")!.value as string[];
    if (!path.length) return { kind: "string", options: null };

    const values = sampleValuesForPath(this.features, path);
    const kind = detectValueKind(values);
    return { kind, options: kind === "string" ? distinctStringOptions(values) : null };
  }

  addToUsedColors(color: string): void {
    if (!this.usedColors.includes(color)) this.usedColors.push(color);
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.isFormValid) return;

    const defaultStyle = buildFlatProperties(this.form.get("default") as FormGroup);
    const textOverride = this.labelAppliesToAll ? extractTextProperties(defaultStyle) : null;
    const styleDefinition: IVectorStyle = !this.rules.length
      ? ({ type: "flat", properties: defaultStyle } as IFlatStyle)
      : ({
          type: "rules",
          rules: [...this.rules.controls.map((g) => buildConditionRule(g, textOverride)), { else: true, style: defaultStyle }],
        } as IRulesStyle);

    this.dialogRef.close({ status: 200, styleDefinition });
  }
}
